import { type ApiEnvelope, api, unwrap } from "@/lib/api";

export interface DeliveryRegion {
	id: string;
	code: string;
	name: string;
	chefLieu: string | null;
	defaultFee: number;
	isActive: boolean;
	sort: number;
	cityCount: number;
	activeCityCount: number;
}

export interface DeliveryCity {
	id: string;
	regionCode: string;
	name: string;
	fee: number;
	expressFee: number | null;
	freeThreshold: number | null;
	etaMinHours: number;
	etaMaxHours: number;
	hubId: string | null;
	isActive: boolean;
	sort: number;
}

/**
 * Quartier desservi d'une ville.
 *
 * C'est lui qui porte le tarif réellement facturé au checkout : la ville ne
 * sert plus que de repli pour les quartiers non catalogués.
 */
export interface DeliveryNeighborhood {
	id: string;
	cityId: string;
	name: string;
	fee: number;
	expressFee: number | null;
	etaMinHours: number;
	etaMaxHours: number;
	isActive: boolean;
	sort: number;
}

export interface UpsertNeighborhoodInput {
	cityId?: string;
	name?: string;
	fee?: number;
	expressFee?: number | null;
	etaMinHours?: number;
	etaMaxHours?: number;
	isActive?: boolean;
	sort?: number;
}

export interface UpsertRegionInput {
	name?: string;
	chefLieu?: string | null;
	defaultFee?: number;
	isActive?: boolean;
	sort?: number;
}

export interface UpsertCityInput {
	regionCode?: string;
	name?: string;
	fee?: number;
	expressFee?: number | null;
	freeThreshold?: number | null;
	etaMinHours?: number;
	etaMaxHours?: number;
	hubId?: string | null;
	isActive?: boolean;
	sort?: number;
}

export const deliveryApi = {
	regions: () =>
		unwrap<DeliveryRegion[]>(
			api.get<ApiEnvelope<DeliveryRegion[]>>("/admin/delivery/regions"),
		),

	updateRegion: (id: string, body: UpsertRegionInput) =>
		unwrap<DeliveryRegion>(
			api.patch<ApiEnvelope<DeliveryRegion>>(
				`/admin/delivery/regions/${id}`,
				body,
			),
		),

	cities: (region?: string) =>
		unwrap<DeliveryCity[]>(
			api.get<ApiEnvelope<DeliveryCity[]>>(
				`/admin/delivery/cities${region ? `?region=${encodeURIComponent(region)}` : ""}`,
			),
		),

	createCity: (body: UpsertCityInput) =>
		unwrap<DeliveryCity>(
			api.post<ApiEnvelope<DeliveryCity>>("/admin/delivery/cities", body),
		),

	updateCity: (id: string, body: UpsertCityInput) =>
		unwrap<DeliveryCity>(
			api.patch<ApiEnvelope<DeliveryCity>>(
				`/admin/delivery/cities/${id}`,
				body,
			),
		),

	removeCity: (id: string) => api.delete(`/admin/delivery/cities/${id}`),

	neighborhoods: (cityId?: string) =>
		unwrap<DeliveryNeighborhood[]>(
			api.get<ApiEnvelope<DeliveryNeighborhood[]>>(
				`/admin/delivery/neighborhoods${cityId ? `?cityId=${encodeURIComponent(cityId)}` : ""}`,
			),
		),

	createNeighborhood: (body: UpsertNeighborhoodInput) =>
		unwrap<DeliveryNeighborhood>(
			api.post<ApiEnvelope<DeliveryNeighborhood>>(
				"/admin/delivery/neighborhoods",
				body,
			),
		),

	updateNeighborhood: (id: string, body: UpsertNeighborhoodInput) =>
		unwrap<DeliveryNeighborhood>(
			api.patch<ApiEnvelope<DeliveryNeighborhood>>(
				`/admin/delivery/neighborhoods/${id}`,
				body,
			),
		),

	removeNeighborhood: (id: string) =>
		api.delete(`/admin/delivery/neighborhoods/${id}`),
};
